import { FC, Fragment } from "react";
import { useDuckFullscreen } from "duck/ui/chat/hooks/useDuckFullscreen";
import MessageFeedback from "duck/ui/chat/MessageFeedback";
import { AssistantMessage, ChatMessage } from "duck/ui/chat/types";

import { useViewport } from "shared/contexts/ViewportContext";

import AssistantContentRenderer from "./AssistantContentRenderer";
import { backQuackRendererRegistry } from "./index";

interface Props {
  message: ChatMessage;
  isStreaming: boolean;
  isHovered?: boolean;
  isLastAIMessage?: boolean;
  threadId?: string; // optional: override for subthreads
}

const MessageContentRenderer: FC<Props> = ({
  message,
  isStreaming,
  isHovered = false,
  isLastAIMessage = false,
  threadId,
}) => {
  const { isMobile } = useViewport();
  const { isFullscreen } = useDuckFullscreen();
  // If we have structured state (parsed JSON), use that
  // Otherwise, fall back to the raw content
  const contentToRender: AssistantMessage = message.state || { messages: [] };
  const backQuack = message.backQuack;

  const allBackQuackRenderers = backQuackRendererRegistry.getAllRenderers();

  // Show feedback if this specific message is complete (has endedAt)
  // and we're not on mobile device (viewport)
  const showFeedback = Boolean(message.endedAt) && !isMobile;

  return (
    <div data-testid="duck-ai-message">
      {allBackQuackRenderers?.map((renderer, idx) => (
        <Fragment key={`${renderer.type}-${idx}`}>
          {backQuack && renderer.canRender(backQuack)
            ? renderer.render(backQuack, { message, isStreaming, isFullscreen })
            : null}
        </Fragment>
      ))}
      <AssistantContentRenderer
        content={contentToRender}
        isStreaming={isStreaming}
        message={message}
      />
      {showFeedback && (
        <MessageFeedback
          message={message}
          isHovered={isHovered}
          isLastAIMessage={isLastAIMessage}
          threadId={threadId}
        />
      )}
    </div>
  );
};

export default MessageContentRenderer;
